/**
 * Centralised time formatter.
 *
 * The family-level `time_format` setting controls clock display across
 * the whole app. Three values:
 *   "auto" → respects the browser locale (default; en-US shows 2:30 PM,
 *           Norwegian / en-GB / etc. show 14:30)
 *   "24h"  → force 24-hour ("14:30") regardless of locale
 *   "12h"  → force 12-hour ("2:30 PM") regardless of locale
 *
 * Components don't read settings directly — `Dashboard.js` and the
 * settings load both call `setTimeFormat()` once on settings change,
 * which stamps the value onto `window.__famfeel_time_format`. Every
 * `fmtTime` call reads that var, so adding the setting to a component
 * doesn't require threading props through nine layers of children.
 *
 * Falls back to a live `Intl.DateTimeFormat` test on first call —
 * never throws on bad input.
 */

const GLOBAL_KEY = "__famfeel_time_format";

export function setTimeFormat(format) {
  // Tolerate junk input — anything we don't recognise → "auto".
  const v = (format || "").toString().toLowerCase();
  const next = v === "24h" || v === "12h" ? v : "auto";
  if (typeof window !== "undefined") {
    window[GLOBAL_KEY] = next;
  }
}

export function getTimeFormat() {
  if (typeof window === "undefined") return "auto";
  return window[GLOBAL_KEY] || "auto";
}

/**
 * Format an ISO timestamp (or a Date) as HH:MM in the family's chosen
 * format. Returns an empty string for null / undefined / unparseable
 * input — never throws, never renders "NaN:NaN".
 */
export function fmtTime(input) {
  if (!input) return "";
  let d;
  try {
    d = input instanceof Date ? input : new Date(input);
  } catch {
    return "";
  }
  if (!d || Number.isNaN(d.getTime())) return "";
  const fmt = getTimeFormat();
  // Locale choice:
  //   - "24h" force → use a known 24h locale ("en-GB") with hour12:false
  //     so the output is unambiguous "14:30".
  //   - "12h" force → use "en-US" with hour12:true → "2:30 PM".
  //   - "auto" → no locale arg, browser picks. Works everywhere.
  let opts;
  let locale;
  if (fmt === "24h") {
    locale = "en-GB";
    opts = { hour: "2-digit", minute: "2-digit", hour12: false };
  } else if (fmt === "12h") {
    locale = "en-US";
    opts = { hour: "2-digit", minute: "2-digit", hour12: true };
  } else {
    locale = undefined; // browser default
    opts = { hour: "2-digit", minute: "2-digit" };
  }
  try {
    return d.toLocaleTimeString(locale, opts);
  } catch {
    // Locale-arg failure on weird browsers — fall back to default.
    return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  }
}
